import React from 'react';
import * as yup from 'yup';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { useSelector } from 'react-redux';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from 'react-fetching-library';
import { mergeOrderDocumentsAction } from '../../../../../../../services/request';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';

// Schemat walidacji Yup
const schema = yup.object({
  documentType: yup.string().required('Typ dokumentu jest wymagany'),
  documentName: yup.string().required('Nazwa jest wymagana'),
  documentWidth: yup.string().required('Jakość jest wymagana'),
  files: yup
    .array()
    .of(
      yup.object({
        pageNumber: yup
          .number()
          .typeError('Kolejność musi być liczbą')
          .required('Numer strony jest wymagany')
      })
    )
    .test('uniquePageNumbers', 'Numery muszą być unikalne', files => {
      const pageNumbers = files.map(file => file.pageNumber);

      return new Set(pageNumbers).size === pageNumbers.length;
    })
});

const PositionModal = ({ formik, openModal, onClose }) => {
  const { order } = useSelector(state => state.orderData);
  const { error, mutate } = useMutation(mergeOrderDocumentsAction);

  const { isDragActive, getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    multiple: true,
    onDropAccepted(files) {
      handleUploadFiles(files);
    },
    onDropRejected() {
      toast.error('Wystpąpił błąd');
    }
  });

  const handleUploadFiles = files => {
    files.forEach(file => {
      append({ file, pageNumber: '' }); // Dodanie pliku do files
    });
  };

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema), // Używamy yupResolver z naszym schematem Yup
    defaultValues: {
      documentType: '',
      documentName: '',
      documentWidth: '1200',
      files: []
    }
  });

  const documentType = watch('documentType');
  const files = watch('files');

  // useEffect, który zaktualizuje nazwę dokumentu po zmianie typu dokumentu
  React.useEffect(() => {
    if (documentType) {
      // Ustawienie wartości nazwy pliku na podstawie typu dokumentu
      const selectedTypeLabel = order.attachments_types_choices.find(
        option => option.value === documentType
      )?.label;
      if (selectedTypeLabel) {
        const initials = selectedTypeLabel
          .split(' ')
          .map(word => word[0].toUpperCase())
          .join('');
        const fullName = `${initials === 'I' ? 'DOC' : initials} ${
          order.participant_name
        } ${order.agreement_number}`;
        setValue('documentName', fullName);
      }
    }
  }, [documentType]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'files'
  });

  const handleAddFile = e => {
    const file = e.target.files[0];
    if (file) {
      append({ file, pageNumber: '' });
      e.target.value = null; // Clear the input
    }
  };

  const handleRemoveFile = index => {
    remove(index);
  };

  const onSubmit = async data => {
    console.log('Submitted data:', data);

    const formData = new FormData();
    formData.append('documentType', data.documentType);
    formData.append('documentName', data.documentName);
    formData.append('documentWidth', data.documentWidth);

    // Dodaj pliki do FormData
    data.files.forEach((fileData, index) => {
      formData.append(`file${index}`, fileData.file);
      formData.append(`file${index}_order`, fileData.pageNumber);
    });

    // Wywołaj akcję z FormData
    const { error, payload } = await mutate(formData, order.id);

    if (!error) {
      toast.success('Dokument został poprawnie dodany');
      console.log('response:', payload);
      formik.setFieldValue('attachments', [
        ...formik.values.attachments,
        payload
      ]);

      // formik.setFieldValue('attachments', prev => [...prev, payload]);

      reset();
      onClose();
    } else {
      toast.error('Nie udało się dodać dokumentu');
    }
  };

  return (
    <Dialog open={openModal} onClose={onClose} fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle color="primary">Dodaj pozycję</DialogTitle>
        <DialogContent>
          <Stack spacing={4} alignItems="flex-start">
            <Controller
              name="documentType"
              control={control}
              render={({ field }) => (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ width: '100%' }}
                >
                  <Typography variant="subtitle2" sx={{ width: '200px' }}>
                    Typ załącznika
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    label=""
                    {...field}
                    error={Boolean(errors.documentType)}
                    helperText={errors.documentType?.message}
                  >
                    {/* Generowanie opcji na podstawie danych z order.attachments_types_choices */}
                    {order.attachments_types_choices.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              )}
            />
            <Controller
              name="documentName"
              control={control}
              render={({ field }) => (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ width: '100%' }}
                >
                  <Typography variant="subtitle2" sx={{ width: '100px' }}>
                    Nazwa pliku
                  </Typography>
                  <TextField
                    fullWidth
                    label=""
                    {...field}
                    error={Boolean(errors.documentName)}
                    helperText={errors.documentName?.message}
                  ></TextField>
                </Stack>
              )}
            />
            <Controller
              name="documentWidth"
              control={control}
              render={({ field }) => (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ width: '100%' }}
                >
                  <Typography variant="subtitle2" sx={{ width: '100px' }}>
                    Jakość
                  </Typography>
                  <TextField
                    fullWidth
                    label=""
                    {...field}
                    error={Boolean(errors.documentWidth)}
                    helperText={errors.documentWidth?.message}
                  ></TextField>
                </Stack>
              )}
            />

            <div
              {...getRootProps({ className: 'dropzone' })}
              style={{
                border: isDragActive ? '2px dashed #cccccc' : 'none',
                padding: '20px',
                textAlign: 'center',
                width: '100%',
                minHeight: '160px',
                display: 'flex',
                justifyContent: 'start',
                alignItems: 'center'
              }}
            >
              <input {...getInputProps()} />
              <Button
                variant="contained"
                color="primary"
                component="label"
                startIcon={<AddPhotoAlternateIcon fontSize="large" />}
              >
                Wybierz plik
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/tiff,image/heic,application/pdf"
                  hidden
                  onChange={handleAddFile}
                />
              </Button>
            </div>

            {files.length > 0 && (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nazwa pliku</TableCell>
                    <TableCell>Kolejność</TableCell>
                    <TableCell>Akcje</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.file.name}</TableCell>
                      <TableCell>
                        <TextField
                          {...register(`files[${index}].pageNumber`, {
                            required: 'Numer strony jest wymagany' // Walidacja, aby pole było wymagane
                          })}
                          type="number"
                          size="small"
                          placeholder="Kolejność lub nr strony"
                          error={Boolean(errors.files?.[index]?.pageNumber)}
                          helperText={
                            errors.files?.[index]?.pageNumber?.message
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleRemoveFile(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
          {errors.files && errors.files.message && (
            <Typography color="error">{errors.files.message}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Anuluj</Button>
          <Button type="submit" variant="contained">
            Dodaj
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PositionModal;
